// RehearsalRoomScreen — lifted from the MagicPatterns mockup (handoff
// 2026-06-25) and wired to live arc state. Replaces the v0 RehearsalRoom.
//
// Data changes (only):
//   - Title + character pulled from arc.parsedScript + arc.selectedCharacter
//   - Tap-to-enter the armed room navigates to the matching route:
//       Binder       → disabled for v0 (no journal in scope)
//       Run Lines    → /run-lines
//       Recording    → /record
//       Drills       → disabled for v0
//       Goals        → disabled for v0
//   - Cover image: leather-cover CSS fallback (per handoff: preserve both
//     image + CSS paths; this script has no generated cover yet)
import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useArc } from '../state/arc';

// Plain touch handlers replace framer-motion drag — drag was eating
// pointer-ups on the room labels and the enter dot on iOS Safari
// (Ryan iPhone test 2026-06-25).

type Room = { id: string; label: string; desc: string; route: string | null };

const ROOMS: Room[] = [
  { id: 'binder',    label: 'Binder',     desc: 'Script, Journal & Research', route: null },
  { id: 'run-lines', label: 'Run Lines',  desc: 'Rehearse the scene',          route: '/run-lines' },
  { id: 'recording', label: 'Recording',  desc: 'Enter the booth',             route: '/record' },
  { id: 'drills',    label: 'Drills',     desc: 'Targeted memory drills',      route: null },
  { id: 'goals',     label: 'Goals',      desc: 'Your progress, off book',     route: null },
];

const ITEM_WIDTH = 150;
const SWIPE_THRESHOLD = 45;

export function RehearsalRoomScreen() {
  const navigate = useNavigate();
  const { state } = useArc();
  const script = state.parsedScript;
  const character = state.selectedCharacter;

  // Default to "Recording" if no take exists yet, otherwise "Run Lines".
  // (Both are valid v0 entry points; the others are disabled.)
  const defaultIdx = state.takeId ? 1 : 2;
  const [armedIdx, setArmedIdx] = useState(defaultIdx);
  const [isDragging, setIsDragging] = useState(false);
  const [entering, setEntering] = useState(false);

  const armed = ROOMS[armedIdx];
  const canEnter = !!armed.route;

  const step = (dir: number) => {
    setArmedIdx((prev) => Math.min(ROOMS.length - 1, Math.max(0, prev + dir)));
  };

  // Plain touch handlers — replaces framer-motion drag which was eating
  // taps on iOS Safari.
  const touchStartX = useRef<number | null>(null);
  const touchStartT = useRef<number>(0);
  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    touchStartT.current = performance.now();
    setIsDragging(false);
  }
  function onTouchMove() {
    if (touchStartX.current !== null) setIsDragging(true);
  }
  function onTouchEnd(e: React.TouchEvent) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dt = performance.now() - touchStartT.current;
    const v = Math.abs(dx) / Math.max(1, dt) * 1000;
    if (dx < -SWIPE_THRESHOLD || (dx < -10 && v > 350)) step(1);
    else if (dx > SWIPE_THRESHOLD || (dx > 10 && v > 350)) step(-1);
    touchStartX.current = null;
    window.setTimeout(() => setIsDragging(false), 50);
  }

  const handleEnter = () => {
    if (!canEnter || !armed.route) return;
    setEntering(true);
    setTimeout(() => navigate(armed.route!), 600);
  };

  // Guard — bounce to library if no script in arc.
  if (!script) {
    return (
      <div className="h-full w-full bg-cream-light flex items-center justify-center text-gray-600 p-8 text-center">
        <p className="font-serif italic">No script loaded. Tap your invite link.</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-cream-light relative overflow-hidden flex flex-col items-center text-gray-900">
      {/* Editorial Header */}
      <div className="w-full pt-14 px-7 flex justify-between items-start relative z-20">
        <div className="flex flex-col">
          <h1 className="font-display text-gray-900 text-2xl tracking-wide uppercase leading-none mb-1">
            {script.title}
          </h1>
          {character && (
            <h2 className="font-display text-gray-500 text-lg tracking-wider uppercase">
              {character}
            </h2>
          )}
        </div>
        <button
          onClick={() => navigate('/library')}
          className="text-right font-mono text-[10px] uppercase tracking-widest text-gray-400 active:text-gray-700 transition-colors"
          aria-label="Back to Library"
        >
          ← Library
        </button>
      </div>

      {/* The script cover — leather CSS fallback */}
      <div className="flex-1 flex items-center justify-center relative z-10 w-full mt-4">
        <div className="relative w-[260px] h-[260px] flex items-center justify-center">
          {/* Aurora ripples behind the manuscript */}
          <motion.div
            className="absolute inset-0 rounded-full aurora-ripple"
            animate={{ scale: [0.8, 1.2, 0.8], opacity: [0.1, 0.3, 0.1] }}
            transition={{ duration: 8, ease: 'easeInOut', repeat: Infinity }}
          />
          <motion.div
            className="absolute inset-0 rounded-full aurora-ripple"
            animate={{ scale: [1, 1.4, 1], opacity: [0.05, 0.2, 0.05] }}
            transition={{ duration: 12, ease: 'easeInOut', repeat: Infinity, delay: 2 }}
          />

          <motion.div
            className="relative z-10"
            animate={{
              scale: entering ? 1.06 : isDragging ? 0.94 : 1,
              opacity: isDragging ? 0.8 : 1,
              y: [0, -10, 0],
            }}
            transition={{
              scale: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
              y: { duration: 6, ease: 'easeInOut', repeat: Infinity },
            }}
          >
            <div className="leather-cover leather-navy" style={{ width: 150, height: 237 }}>
              <div className="leather-spine" />
              <div className="manuscript-rule" />
              <div className="brass-rivet top-[14%]" />
              <div className="brass-rivet top-1/2 -translate-y-1/2" />
              <div className="brass-rivet bottom-[14%]" />
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center pl-5 pr-3">
                <h2 className="font-display leading-[0.92] tracking-wide gold-foil text-[22px] break-words">
                  {script.title.toUpperCase()}
                </h2>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Navigator — swipe to step, tap to enter. Plain touch
          handlers (per Ryan iPhone test 2026-06-25 — framer-motion drag
          was eating the dot tap and label taps on iOS Safari). */}
      <div
        className="relative w-full h-[210px] flex justify-center z-20 mt-auto select-none"
        style={{ touchAction: 'pan-y' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Soft arc baseline */}
        <div className="absolute top-[78px] left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full border-t border-gray-200/70 pointer-events-none" />

        {/* Pointer = the enter action. Tap target 56×56 (iOS guideline 44+). */}
        <div className="absolute top-[-8px] left-1/2 -translate-x-1/2 flex flex-col items-center z-30">
          <motion.button
            onClick={() => { if (!isDragging) handleEnter(); }}
            whileTap={{ scale: 0.9 }}
            disabled={!canEnter}
            aria-label={canEnter ? `Enter ${armed.label}` : `${armed.label} unavailable`}
            className={`relative flex items-center justify-center w-14 h-14 focus:outline-none ${!canEnter ? 'opacity-30' : ''}`}
          >
            <motion.span
              className="absolute rounded-full border border-gray-900/20"
              animate={entering ? { scale: [1, 2.6], opacity: [0.5, 0] } : { scale: [1, 1.9, 1], opacity: [0.35, 0, 0.35] }}
              transition={{ duration: entering ? 0.9 : 3.2, ease: 'easeOut', repeat: entering ? 0 : Infinity }}
              style={{ width: 14, height: 14 }}
            />
            <motion.span
              className="rounded-full dot-light block"
              animate={{ scale: entering ? 1.9 : [1, 1.18, 1] }}
              transition={entering ? { duration: 0.45, ease: [0.22, 1, 0.36, 1] } : { duration: 3.2, ease: 'easeInOut', repeat: Infinity }}
              style={{ width: 8, height: 8 }}
            />
          </motion.button>
          <div className="w-px h-3 bg-gray-900/25 mt-1.5" />
        </div>

        {/* Room track */}
        <div className="absolute top-[52px] left-0 right-0 overflow-hidden h-[60px]">
          <motion.div
            className="flex items-start"
            style={{ marginLeft: `calc(50% - ${ITEM_WIDTH / 2}px)` }}
            animate={{ x: -(armedIdx * ITEM_WIDTH) }}
            transition={{ type: 'spring', stiffness: 320, damping: 32, mass: 0.9 }}
          >
            {ROOMS.map((room, i) => {
              const isArmed = i === armedIdx;
              const distance = Math.abs(i - armedIdx);
              const disabled = !room.route;
              return (
                <button
                  key={room.id}
                  onClick={() => {
                    if (isDragging) return;
                    if (!isArmed) setArmedIdx(i);
                    else handleEnter();
                  }}
                  style={{ width: ITEM_WIDTH, minHeight: 44 }}
                  className="flex flex-col items-center shrink-0 focus:outline-none"
                >
                  <motion.span
                    animate={{
                      opacity: isArmed ? (disabled ? 0.55 : 1) : distance === 1 ? 0.4 : 0.18,
                      scale: isArmed ? 1 : 0.82,
                    }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className={`font-mono uppercase tracking-[0.2em] whitespace-nowrap ${isArmed ? 'text-gray-900 font-bold text-[13px]' : 'text-gray-400 text-[11px]'}`}
                  >
                    {room.label}
                  </motion.span>
                </button>
              );
            })}
          </motion.div>
        </div>

        {/* Chevron hints — 44×44 tap target per iOS guideline. */}
        <button
          onClick={() => { if (!isDragging) step(-1); }}
          className={`absolute top-[46px] left-2 z-30 w-11 h-11 flex items-center justify-center transition-opacity duration-300 ${armedIdx === 0 ? 'opacity-0 pointer-events-none' : 'opacity-50 active:opacity-100'}`}
          aria-label="Previous room"
        >
          <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
        </button>
        <button
          onClick={() => { if (!isDragging) step(1); }}
          className={`absolute top-[46px] right-2 z-30 w-11 h-11 flex items-center justify-center transition-opacity duration-300 ${armedIdx === ROOMS.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-50 active:opacity-100'}`}
          aria-label="Next room"
        >
          <ChevronRightIcon className="w-5 h-5 text-gray-700" />
        </button>

        {/* Under the arch — a quiet line describing the armed room */}
        <div className="absolute bottom-[44px] left-1/2 -translate-x-1/2 z-20 w-full px-8 flex flex-col items-center gap-3 pointer-events-none text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={armedIdx}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: entering ? 0.4 : 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif italic text-gray-500 text-[15px] leading-snug"
            >
              {entering
                ? `Entering ${armed.label}…`
                : canEnter
                ? armed.desc
                : `${armed.desc} · coming soon`}
            </motion.p>
          </AnimatePresence>
          <span className="font-mono text-[8px] uppercase tracking-[0.32em] text-gray-300">
            Swipe to choose · tap to enter
          </span>
        </div>
      </div>
    </div>
  );
}
